import React from 'react';
import { SafetyOutlined, EyeOutlined, AimOutlined } from '@ant-design/icons';
import useReveal from '../useReveal';
import './AIEControlSection.css';

/**
 * AIEControlSection — the merged "ownership + control" section (Nitish, Sep 1
 * 2026, option C2 ruled from a rendered mock: statement left, three claim rows
 * right). Replaces the two AIERailsSection mounts ("True ownership" and
 * "Control"): six cards became three; the dropped cards duplicated wall panes
 * 02/04/05/07. No approval-card artifact here on purpose (his ruling): the
 * wall's Cowork pane already renders a real one, so this section states the
 * position and the wall proves it.
 *
 * RULED COPY IS LOCKED COPY: every string below was approved on the mock
 * (Maeve's work/aie_econ_control, CEL-1990). Change a string only with his
 * explicit yes on the exact wording.
 *
 * Reuse (Sep 23 2026): role pages render this SAME layout twice (Control and
 * "What it won't do") by passing their own copy. A row without an `Icon`
 * renders text only (his ruling for "What it won't do": no icons). Defaults
 * are the /ai-employees copy, so that page renders exactly as before.
 */
const ROWS = [
  {
    Icon: SafetyOutlined,
    title: 'Nothing risky without your yes',
    body: 'You define what needs approval for the role. They ask first, every time.',
  },
  {
    Icon: EyeOutlined,
    title: 'Everything visible, always',
    body: 'Every task, email and decision on one board. Redirect with one message.',
  },
  {
    Icon: AimOutlined,
    title: 'Manage output, not prompt',
    body: 'They plan the work, run it and report back.',
  },
];

const DEFAULT_SUB = (
  <>
    You are in the driver&rsquo;s seat for critical decisions.{' '}
    <span className="aie-ctl__sub-accent">They are in the driver&rsquo;s seat for work.</span>
  </>
);

const AIEControlSection = ({
  eyebrow = 'Control',
  statement = 'An owner, not an operator.',
  sub = DEFAULT_SUB,
  rows = ROWS,
  className = '',
}) => {
  const [ref, visible] = useReveal();

  return (
    <section className={`aie-ctl aie-section${className ? ` ${className}` : ''}`}>
      <div className="aie-ctl__split">
        <div className="aie-ctl__statement-col">
          <span className="aie-eyebrow">{eyebrow}</span>
          <h2 className="aie-ctl__statement aie-display">{statement}</h2>
          <p className="aie-ctl__sub">{sub}</p>
        </div>
        <div className={`aie-ctl__rows aie-reveal ${visible ? 'is-visible' : ''}`} ref={ref}>
          {rows.map(({ Icon, title, body }) => (
            <div className={`aie-ctl__row${Icon ? '' : ' aie-ctl__row--text'}`} key={title}>
              {Icon && <span className="aie-ctl__icon"><Icon /></span>}
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIEControlSection;
